import { useMemo } from "react";
import type { Project } from "./useDashboardData";

// Helper function to parse budget strings into numbers
export const parseBudgetToNumber = (budget: string | null | undefined): number => {
  if (!budget || typeof budget !== "string") return 0;

  const budgetLower = budget.toLowerCase();
  if (budgetLower.includes("under-10k") || budgetLower.includes("under 10k")) return 7500; // mid-point
  if (budgetLower.includes("10k-20k") || budgetLower.includes("10-20k")) return 15000;
  if (budgetLower.includes("20k-35k") || budgetLower.includes("20-35k")) return 27500;
  if (budgetLower.includes("35k-50k") || budgetLower.includes("35-50k")) return 42500;
  if (budgetLower.includes("50k+") || budgetLower.includes("over 50k")) return 60000;

  // Try to extract numbers from custom budget strings
  const numbers = budget.match(/\d+/g);
  if (numbers && numbers.length > 0) {
    const firstNumber = Number.parseInt(numbers[0], 10);
    if (firstNumber > 1000) return firstNumber; // Assume it's already in dollars
    if (firstNumber > 0) return firstNumber * 1000; // Assume it's in thousands
  }

  return 0; // Default fallback
};

interface UseDashboardAnalyticsProps {
  projectsData: Project[] | undefined;
  allCustomersData: any[] | undefined;
}

export function useDashboardAnalytics({
  projectsData,
  allCustomersData,
}: UseDashboardAnalyticsProps) {
  // Calculate project statistics and revenue metrics
  const projectStats = useMemo(() => {
    if (!projectsData) {
      return {
        total: 0,
        draft: 0,
        active: 0,
        pending: 0,
        completed: 0,
        totalPipelineValue: 0,
        activeProjectsValue: 0,
        completedProjectsValue: 0,
        averageProjectSize: 0,
      };
    }

    return {
      total: projectsData.length,
      draft: projectsData.filter((p) => p.status === "draft").length,
      active: projectsData.filter((p) => p.status === "active").length,
      pending: projectsData.filter((p) => ["pending", "sent", "under-review"].includes(p.status))
        .length,
      completed: projectsData.filter((p) => ["completed", "accepted"].includes(p.status)).length,
      // Revenue calculations
      totalPipelineValue: projectsData.reduce((sum, p) => sum + parseBudgetToNumber(p.budget), 0),
      activeProjectsValue: projectsData
        .filter((p) => ["active", "pending", "sent", "under-review"].includes(p.status))
        .reduce((sum, p) => sum + parseBudgetToNumber(p.budget), 0),
      completedProjectsValue: projectsData
        .filter((p) => ["completed", "accepted"].includes(p.status))
        .reduce((sum, p) => sum + parseBudgetToNumber(p.budget), 0),
      averageProjectSize:
        projectsData.length > 0
          ? projectsData.reduce((sum, p) => sum + parseBudgetToNumber(p.budget || ""), 0) /
            projectsData.length
          : 0,
    };
  }, [projectsData]);

  // Calculate customer analytics
  const customerAnalytics = useMemo(() => {
    if (!(allCustomersData && projectsData)) {
      return {
        totalCustomers: 0,
        newCustomersThisMonth: 0,
        customersWithProjects: 0,
        topCustomers: [],
        averageProjectsPerCustomer: 0,
        customerGrowthRate: 0,
      };
    }

    const now = new Date();
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    // Group projects by customer
    const projectsByCustomer = projectsData.reduce(
      (acc, project) => {
        const customerKey =
          project.customer_name ||
          `${project.first_name || ""} ${project.last_name || ""}`.trim() ||
          "Unknown Customer";

        if (!acc[customerKey]) {
          acc[customerKey] = [];
        }
        acc[customerKey].push(project);
        return acc;
      },
      {} as Record<string, typeof projectsData>
    );

    // Calculate top customers by project count and value
    const topCustomers = Object.entries(projectsByCustomer)
      .map(([customerName, projects]) => ({
        name: customerName,
        projectCount: projects.length,
        totalValue: projects.reduce((sum, p) => sum + parseBudgetToNumber(p.budget), 0),
        completedProjects: projects.filter((p) => ["completed", "accepted"].includes(p.status))
          .length,
        activeProjects: projects.filter((p) =>
          ["active", "pending", "sent", "under-review"].includes(p.status)
        ).length,
      }))
      .sort((a, b) => b.totalValue - a.totalValue)
      .slice(0, 5);

    // Calculate new customers this month
    const newCustomersThisMonth = allCustomersData.filter(
      (customer) => new Date(customer.created_at) >= thisMonth
    ).length;

    // Calculate growth rate (simplified - comparing this month vs last month)
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
    const customersLastMonth = allCustomersData.filter((customer) => {
      const createdDate = new Date(customer.created_at);
      return createdDate >= lastMonth && createdDate <= lastMonthEnd;
    }).length;

    const growthRate =
      customersLastMonth > 0
        ? ((newCustomersThisMonth - customersLastMonth) / customersLastMonth) * 100
        : newCustomersThisMonth > 0
          ? 100
          : 0;

    return {
      totalCustomers: allCustomersData.length,
      newCustomersThisMonth,
      customersWithProjects: Object.keys(projectsByCustomer).length,
      topCustomers,
      averageProjectsPerCustomer:
        Object.keys(projectsByCustomer).length > 0
          ? projectsData.length / Object.keys(projectsByCustomer).length
          : 0,
      customerGrowthRate: growthRate,
    };
  }, [allCustomersData, projectsData]);

  return {
    projectStats,
    customerAnalytics,
  };
}
